import React, { useCallback, useEffect, useState, createContext, useContext } from 'react';
import { Product, CartItem } from '../types';
interface CartContextType {
  items: CartItem[];
  addToCart: (product: Product) => void;
  removeFromCart: (productId: string) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  clearCart: () => void;
  totalItems: number;
  subtotal: number;
  ebtTotal: number;
  isInCart: (productId: string) => boolean;
  getQuantity: (productId: string) => number;
}
const CartContext = createContext<CartContextType | undefined>(undefined);
const CART_STORAGE_KEY = 'diamond-mart-cart';
interface CartProviderProps {
  children: ReactNode;
}
export function CartProvider({
  children
}: CartProviderProps) {
  const [items, setItems] = useState<CartItem[]>(() => {
    if (typeof window === 'undefined') return [];
    try {
      const stored = localStorage.getItem(CART_STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
    } catch (error) {
      console.error('Failed to save cart to localStorage:', error);
    }
  }, [items]);
  const addToCart = useCallback((product: Product) => {
    setItems(currentItems => {
      const existingItem = currentItems.find(item => item.product.id === product.id);
      if (existingItem) {
        return currentItems.map(item => item.product.id === product.id ? {
          ...item,
          quantity: item.quantity + 1
        } : item);
      }
      return [...currentItems, {
        product,
        quantity: 1
      }];
    });
  }, []);
  const removeFromCart = useCallback((productId: string) => {
    setItems(currentItems => currentItems.filter(item => item.product.id !== productId));
  }, []);
  const updateQuantity = useCallback((productId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    setItems(currentItems => currentItems.map(item => item.product.id === productId ? {
      ...item,
      quantity
    } : item));
  }, [removeFromCart]);
  const clearCart = useCallback(() => {
    setItems([]);
  }, []);
  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  const subtotal = items.reduce((sum, item) => sum + item.product.price * item.quantity, 0);
  const ebtTotal = items.filter(item => item.product.isEBT).reduce((sum, item) => sum + item.product.price * item.quantity, 0);
  const isInCart = useCallback((productId: string) => {
    return items.some(item => item.product.id === productId);
  }, [items]);
  const getQuantity = useCallback((productId: string) => {
    const item = items.find(item => item.product.id === productId);
    return item?.quantity ?? 0;
  }, [items]);
  const value: CartContextType = {
    items,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    totalItems,
    subtotal,
    ebtTotal,
    isInCart,
    getQuantity
  };
  return <CartContext.Provider value={value} data-id="element-267">{children}</CartContext.Provider>;
}
export function useCart(): CartContextType {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}